import { z } from 'zod';
import { TASK_KINDS } from '../tasks.ts';
import { defineTool } from './types.ts';

export const speakingTask = defineTool({
  name: 'speaking_task',
  description:
    'Ask the user to speak while the app records: a mock interview question about an experience, skill or situation (kind interview_answer); an open prompt to talk freely for 30-60 seconds, which is what "tell me about yourself" and any introduction are (kind free_speech); or a sentence to read aloud (kind read_aloud, with the exact script; only read_aloud takes a script). After calling it, say one short line like "Take your time, tap Done when you are finished" and then stay silent until the result arrives. The result contains what they said, feedback on the content, usually speechScores (delivery and language), and progress: their level (null until two answers back it up, and while it is null never name a level) and kindsNotYetDone, the kinds of task they have not done yet. Your plan decides which task comes next; kindsNotYetDone is background for it, not something to mention. Never ask extra questions to get a level: it shows up as they practise. React warmly and specifically, one improvement at a time: one content point, then at most one delivery or language point from speechScores (a weak sound, a grammar fix, fillers or pace). Do not read out scores after each answer (the card shows them); say the level only when they ask or when your plan says to. If speechScores.scored is false, give its reason in a few words and move on.',
  params: z
    .object({
      kind: z.enum(TASK_KINDS),
      prompt: z.string().min(5).max(300),
      script: z.string().max(400).optional(),
    })
    // Scored against the script, so a read-aloud without one would be a wasted (capped) scoring call.
    .refine((p) => p.kind !== 'read_aloud' || Boolean(p.script?.trim()), {
      message: 'read_aloud needs the exact sentence in script',
      path: ['script'],
    }),
  execution: 'client',
  behavior: 'NON_BLOCKING',
  responseScheduling: 'WHEN_IDLE',
  fixtures: [
    {
      name: 'interview',
      params: {
        kind: 'interview_answer',
        prompt: 'Tell me about a time you delivered a project under a tight deadline.',
      },
    },
    {
      name: 'free-speech',
      params: {
        kind: 'free_speech',
        prompt: 'Tell me about yourself, the way you would open a Product Manager interview.',
      },
    },
    {
      name: 'read-aloud',
      params: {
        kind: 'read_aloud',
        prompt: 'Read this out loud, at your natural pace.',
        script: 'I led the migration of our payments service and we shipped two days early.',
      },
    },
  ],
});
